/**
 * The /control socket: JSON, main thread, session lifecycle.
 *
 * It is a SEPARATE socket from /media on purpose. A 10 KB audio chunk must never
 * head-of-line-block a transcript delta, and the two must be able to fail
 * independently — "recording uplink down, conversation fine" is a real state
 * that deserves its own name, and it only has a name if the two sockets are two
 * sockets.
 *
 * THE CENTRAL PROPERTY OF THIS FILE: losing it does not lose the call. Media is
 * browser<->OpenAI, so a control drop costs us the transcript relay, the tool
 * spinner and telemetry delivery, and costs the conversation nothing. Therefore
 * this reconnects forever with backoff and NEVER escalates to ending the call.
 * The one thing it must get right on reconnect is `hello.lastSeq`, because that
 * is what turns a media outage into latency rather than loss.
 *
 * TOKEN: `?t=`, AND THE GUESS THIS COMMENT USED TO ADMIT TO WAS WRONG.
 *
 * A browser WebSocket cannot set an Authorization header and the
 * `Sec-WebSocket-Protocol` route is fragile through proxies, so the token goes on
 * the query string. This said `?token=` and called it a guess. edge-api reads `t`
 * and nothing else (`workers/edge-api/src/index.ts`, `upgrade()`), and it answers a
 * missing one with `401 no_token`.
 *
 * IT LOOKED LIKE IT WORKED, WHICH IS WHY IT SURVIVED. `sessions.ts` bakes
 * `?t=<controlToken>` into `controlUrl` and `mediaUrl`, so the first connect
 * authenticated on the token already in the URL while `socketUrl` appended a second
 * parameter nobody read. The reconnect is where it mattered: the URL became
 * `?t=<controlToken>&token=<reconnectToken>`, the server read `t`, and every
 * reconnect therefore presented the 120-second credential. So a drop after two
 * minutes could never recover — exactly the failure the 70-minute reconnect token
 * was added to prevent, with the token itself arriving and being ignored.
 *
 * Setting `t` also makes `sessions.ts:187`'s comment true: it says the client "swaps
 * it in when it re-dials", and `searchParams.set` now does precisely that — it
 * REPLACES the baked-in value rather than sitting beside it.
 *
 * Note that the DO's own hop reads `token` (`session-do/src/index.ts`). That is a
 * different boundary — edge-api forwards there after verifying — and is not this
 * function's business. The two names disagreeing is a trap; the cross-boundary check
 * in `tests/authcheck.mjs` asserts THIS one against edge-api's reader from source.
 */
import { CLOCK_SYNC_BURST, CLOCK_SYNC_INTERVAL_MS, WIRE_VERSION, clockOffsetMs } from '@voiceai/wire'
import type { ClientMsg, ServerMsg } from '@voiceai/wire'
import type { TrackName } from './types'

/** Wall clock with sub-millisecond resolution, as epoch millis. */
export function nowEpochMs(): number {
  return performance.timeOrigin + performance.now()
}

/**
 * Resolve a URL from the session response into an absolute ws:// or wss:// URL
 * and apply the token. Relative is the expected shape (the app is same-origin
 * with the edge in dev via the Vite proxy, and in production too), but an
 * absolute URL is accepted so a deployment can point the sockets elsewhere
 * without a client change.
 */
export function socketUrl(raw: string, token: string): string {
  const base = new URL(raw, location.href)
  if (base.protocol === 'http:') base.protocol = 'ws:'
  else if (base.protocol === 'https:') base.protocol = 'wss:'
  // `set`, not `append`: on a reconnect this must REPLACE the short-lived token
  // that `sessions.ts` baked into the URL, not accompany it.
  base.searchParams.set('t', token)
  return base.toString()
}

export interface ClockSample {
  offsetMs: number
  rttMs: number
}

export interface ControlHooks {
  onMessage(msg: ServerMsg): void
  /** Socket up or down. Down is not fatal; the UI shows a degraded badge. */
  onUp(up: boolean): void
  /** Only fired when a sample IMPROVES on the best one seen so far. */
  onClock(sample: ClockSample): void
  /** Read at hello time, so a reconnect resumes from the right place. */
  lastSeq(): Partial<Record<TrackName, number>>
  onLog(level: 'info' | 'warn' | 'error', message: string): void
}

const RECONNECT_MIN_MS = 250
const RECONNECT_MAX_MS = 8000
/** Spacing inside the opening burst. Tight enough to finish in a second, loose
 *  enough that the samples are independent rather than one queued volley. */
const BURST_SPACING_MS = 120

export class ControlChannel {
  private ws: WebSocket | null = null
  private closed = false
  private attempt = 0
  /** Epoch ms of the close that started the current outage, or null while up.
   *  Reported in the next hello so the server's durable log gets the outage's
   *  true duration and dial count — the failed dials themselves never reach it. */
  private outageStartMs: number | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private syncTimer: ReturnType<typeof setInterval> | null = null
  private burstTimers: ReturnType<typeof setTimeout>[] = []
  private pingId = 1
  private pending = new Map<number, number>()
  private best: ClockSample | null = null

  constructor(
    private readonly url: string,
    /** The same URL with the long-lived reconnect token. See CreateSessionResponse. */
    private readonly reconnectUrl: string,
    private readonly hooks: ControlHooks,
  ) {}

  get up(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  get clock(): ClockSample | null {
    return this.best
  }

  start(): void {
    this.closed = false
    this.open()
  }

  private open(): void {
    if (this.closed) return
    // See the note in Uploader.connect: `attempt === 0` is the first dial, and
    // only the first dial may spend the two-minute token. Everything after it
    // uses the reconnect credential, or a drop at minute six never recovers.
    const ws = new WebSocket(this.attempt === 0 ? this.url : this.reconnectUrl)
    this.ws = ws

    ws.onopen = () => {
      // Captured BEFORE the reset: `attempt` is the dial count of the outage
      // this open ends, and it is about to become 0.
      const attempts = this.attempt
      const outageStart = this.outageStartMs
      this.attempt = 0
      this.outageStartMs = null
      this.hooks.onUp(true)
      const hello: ClientMsg = {
        t: 'hello',
        wire: WIRE_VERSION,
        lastSeq: this.hooks.lastSeq(),
        ua: navigator.userAgent,
        clientEpochMs: nowEpochMs(),
        ...(outageStart !== null
          ? { reconnect: { attempts, downMs: Math.max(0, Math.round(nowEpochMs() - outageStart)) } }
          : {}),
      }
      this.sendRaw(hello)
      this.startClockSync()
    }

    ws.onmessage = (ev) => {
      // t3 must be captured before anything else in the handler, or it measures
      // our own JSON parse instead of the network.
      const t3 = nowEpochMs()
      if (typeof ev.data !== 'string') return
      let msg: ServerMsg
      try {
        msg = JSON.parse(ev.data) as ServerMsg
      } catch {
        this.hooks.onLog('warn', 'control: unparseable message discarded')
        return
      }
      if (msg.t === 'pong') {
        this.absorbPong(msg, t3)
        return
      }
      if (msg.t === 'ready' && msg.wire !== WIRE_VERSION) {
        this.hooks.onLog(
          'warn',
          `wire version mismatch: this tab speaks v${WIRE_VERSION}, the server answered v${msg.wire}. ` +
            'Reload to pick up the current client.',
        )
      }
      this.hooks.onMessage(msg)
    }

    ws.onclose = () => {
      this.stopClockSync()
      if (this.ws === ws) this.ws = null
      // First close of an outage stamps its start; the failed re-dials that
      // follow each fire this handler too and must not move it.
      if (this.outageStartMs === null) this.outageStartMs = nowEpochMs()
      this.hooks.onUp(false)
      this.scheduleReconnect()
    }

    // `onerror` on a WebSocket carries no useful detail by design (it would be a
    // cross-origin information leak), so there is nothing to log here that
    // onclose does not already cover.
    ws.onerror = () => {}
  }

  private scheduleReconnect(): void {
    if (this.closed) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** this.attempt)
    this.attempt += 1
    // Jitter, so that a DO deploy which drops every socket at once does not get
    // every client back in the same millisecond.
    const jittered = delay * (0.7 + Math.random() * 0.6)
    this.reconnectTimer = setTimeout(() => this.open(), jittered)
  }

  // --------------------------------------------------------------------------
  // Clock sync. See the wire contract for the four-timestamp algorithm and for
  // why the MINIMUM-rtt sample wins instead of the average.
  // --------------------------------------------------------------------------

  private startClockSync(): void {
    this.stopClockSync()
    for (let i = 0; i < CLOCK_SYNC_BURST; i++) {
      this.burstTimers.push(setTimeout(() => this.ping(), i * BURST_SPACING_MS))
    }
    this.syncTimer = setInterval(() => this.ping(), CLOCK_SYNC_INTERVAL_MS)
  }

  private stopClockSync(): void {
    for (const t of this.burstTimers) clearTimeout(t)
    this.burstTimers = []
    if (this.syncTimer !== null) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
    this.pending.clear()
  }

  private ping(): void {
    if (!this.up) return
    const id = this.pingId++
    const t0 = nowEpochMs()
    this.pending.set(id, t0)
    // A pong that never comes back would otherwise leak an entry per 10s.
    setTimeout(() => this.pending.delete(id), 30_000)
    this.sendRaw({ t: 'ping', id, t0 })
  }

  private absorbPong(msg: Extract<ServerMsg, { t: 'pong' }>, t3: number): void {
    const t0 = this.pending.get(msg.id) ?? msg.t0
    this.pending.delete(msg.id)
    const sample = clockOffsetMs(t0, msg.t1, msg.t2, t3)
    if (!Number.isFinite(sample.offsetMs) || !Number.isFinite(sample.rttMs)) return
    if (this.best === null || sample.rttMs < this.best.rttMs) {
      this.best = sample
      this.hooks.onClock(sample)
    }
  }

  // --------------------------------------------------------------------------

  private sendRaw(msg: ClientMsg): boolean {
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try {
      ws.send(JSON.stringify(msg))
      return true
    } catch {
      return false
    }
  }

  /**
   * Fire and forget. A dropped control message is NOT retried: every message on
   * this channel is either an intent the human will repeat (`interrupt`) or
   * telemetry that is worth less than the complexity of an outbox. The
   * recorder's resume point is re-derived in `hello`. CallSession separately
   * keeps bounded recorder diagnostics when this method reports a failed send.
   */
  send(msg: ClientMsg): boolean {
    return this.sendRaw(msg)
  }

  close(): void {
    this.closed = true
    this.stopClockSync()
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    const ws = this.ws
    this.ws = null
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close(1000, 'call ended')
      } catch {
        /* nothing to do about a socket that will not close */
      }
    }
  }
}
